import { useCallback, useMemo } from 'react';
import { useLocation, useNavigation, useParams, useSubmit } from 'react-router';

import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import type { PinStorePayload } from '@jane/bloom/routes/$basename.api.session.store/types';
import { IntentSchema } from '@jane/bloom/routes/$basename.api.session.store/types';
import { buildFormData } from '@jane/bloom/utils/formData';
import { queryStringContaining } from '@jane/bloom/utils/queryParams/preservingQueryParams';
import {
  AGE_GATE_QUERY_KEY,
  DRAFT_QUERY_KEY,
  SEARCH_TERM_QUERY_KEY,
} from '@jane/bloom/utils/queryParams/queryParams';

export function usePinStore() {
  const basename = useBloomBasename();
  const location = useLocation();
  const params = useParams();
  const { formAction, state } = useNavigation();
  const pinning = Boolean(
    formAction?.endsWith('api/session/store') && state === 'loading'
  );
  const submit = useSubmit();

  const queryParams = useMemo(() => {
    return queryStringContaining({
      paramKeys: [AGE_GATE_QUERY_KEY, DRAFT_QUERY_KEY, SEARCH_TERM_QUERY_KEY],
      searchParams: new URLSearchParams(location.search),
    });
  }, [location.search]);

  /**
   * Manually pin a store in the user's session
   * @param payloadData - Store data to pin
   */
  const pinStore = useCallback(
    (payloadData: Omit<PinStorePayload, 'intent'>) => {
      let cleanPath = location.pathname.replace(/store\/\d+\//g, '');
      if (params['storeSlug']) {
        cleanPath = cleanPath.replace(
          `${basename}${params['storeSlug']}/`,
          basename
        );
      }
      const redirectTo = `${cleanPath}${queryParams}`;

      const formData: PinStorePayload = {
        intent: IntentSchema.Enum.changeStore,
        redirectTo,
        ...payloadData,
      };

      submit(buildFormData(formData), {
        action: `${basename}api/session/store`,
        method: 'post',
      });
    },
    [basename, submit, location.pathname, queryParams, params]
  );

  return {
    pinning,
    pinStore,
  };
}
